import { useState } from 'react'
import { PencilSquareIcon, TrashIcon, DocumentArrowDownIcon, DevicePhoneMobileIcon, ArrowDownTrayIcon, ListBulletIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { parseDate } from 'utils'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import TableComponent from 'components/common/TableComponent'
import SelectComponent from 'components/common/SelectComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import EditDeviceModal from 'components/modals/EditDeviceModal'
import FarmQRModal from 'components/modals/FarmQRModal'
import DeviceActivitiesModal from 'components/modals/DeviceActivitiesModal'
import ModalComponent from 'components/common/ModalComponent'
import { SERVER } from 'config'

// Newest APK uploaded in App Releases; public so it works as the install link.
const LATEST_APK_URL = `${SERVER.url}/${SERVER.apiPath}/app-releases/latest/apk`

const DevicesGroupsManagement = ({
  devices,
  devicesGroups,
  activities = [],
  onRefreshDevices,
  onRefreshDevicesGroups,
  user
}) => {
  const { t } = useTranslation()
  const [state, setState] = useState({
    selectedDevice: null,
    selectedDeviceGroup: '',
    searchTermDevicesGroups: '',
    showEditModal: false,
    isQrModalOpen: false,
    showActivitiesModal: false,
    selectedDeviceForActivities: null,
    selectedActivityIds: [],
    isSavingActivities: false,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const { selectedDevice, showEditModal, isQrModalOpen, showActivitiesModal } = state

  // Helper for auth headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('secret')}`,
    'Content-Type': 'application/json'
  })

  // Create select options for device groups
  const devicesGroupsSelectOptions = devicesGroups.map(group => ({
    value: group.deviceGroupId.toString(),
    label: group.groupName || t('mgmt.devicesGroups.unnamedGroup')
  }))

  const extractActivityIds = (deviceActivities) => {
    if (!deviceActivities) return []

    let activitiesData = deviceActivities
    if (typeof activitiesData === 'string') {
      try {
        activitiesData = JSON.parse(activitiesData)
      } catch (error) {
        return []
      }
    }

    if (!Array.isArray(activitiesData)) return []

    return activitiesData
      .map(activity => {
        if (typeof activity === 'number') return activity
        if (activity && typeof activity === 'object') return Number(activity.activityId)
        return NaN
      })
      .filter(activityId => Number.isInteger(activityId) && activityId > 0)
  }

  const openActivitiesModal = (device) => {
    setState(s => ({
      ...s,
      selectedDeviceForActivities: device,
      selectedActivityIds: extractActivityIds(device.activities),
      showActivitiesModal: true
    }))
  }

  const closeActivitiesModal = () => {
    setState(s => ({
      ...s,
      showActivitiesModal: false,
      selectedDeviceForActivities: null,
      selectedActivityIds: [],
      isSavingActivities: false
    }))
  }

  const handleToggleActivity = (activityId) => {
    setState(s => {
      const exists = s.selectedActivityIds.includes(activityId)
      return {
        ...s,
        selectedActivityIds: exists
          ? s.selectedActivityIds.filter(id => id !== activityId)
          : [...s.selectedActivityIds, activityId]
      }
    })
  }

  const handleSaveDeviceActivities = async () => {
    if (!state.selectedDeviceForActivities?.deviceId) return

    setState(s => ({ ...s, isSavingActivities: true }))
    try {
      const selectedActivities = state.selectedActivityIds.map(activityId => {
        const activity = activities.find(item => Number(item.activityId) === Number(activityId))
        return {
          activityId: Number(activityId),
          activityName: activity?.activityName
        }
      })

      const response = await makeRequest({
        method: 'PUT',
        url: `/management/devices/activities/${state.selectedDeviceForActivities.deviceId}`,
        data: { activities: selectedActivities },
        headers: getAuthHeaders(),
        alertResult: true
      })

      if (response.status === 200) {
        await onRefreshDevices()
        closeActivitiesModal()
      }
    } catch (error) {
      console.error('Error saving device activities:', error)
      showAlert({ message: t('mgmt.devicesGroups.errSaveActivities'), icon: 'error' })
      setState(s => ({ ...s, isSavingActivities: false }))
    }
  }

  // Map devices with their group names for the top table
  const devicesInGroupsMapping = devices.map(device => {
    const group = devicesGroupsSelectOptions.find(g => String(g.value) === String(device.deviceGroupId))
    return {
      deviceId: device.deviceId,
      type: device.type,
      managerDevice: device.managerDevice,
      identifier: device.identifier,
      deviceGroupId: device.deviceGroupId,
      groupName: group ? group.label : t('mgmt.devicesGroups.unassigned'),
      appVersion: device.appVersion,
      lastSync: device.lastSync,
      activities: device.activities,
      assignedActivities: extractActivityIds(device.activities).length
    }
  })

  // Edit device
  const handleEditDevice = async (device) => {
    try {
      // deviceGroupId only travels when a group is selected — the endpoint takes
      // it as a string and an empty value would fail the integer FK.
      const data = { managerDevice: device.managerDevice }
      if (device.deviceGroupId) data.deviceGroupId = String(device.deviceGroupId)
      const response = await makeRequest({
        method: 'PUT',
        url: `/management/devices/${device.deviceId}`,
        data,
        headers: getAuthHeaders(),
        alertResult: true
      })
      if (response.status === 200) {
        setState(s => ({ ...s, showEditModal: false }))
        await onRefreshDevices()
      }
    } catch (error) {
      console.error('Error editing device:', error)
      setState(s => ({ ...s, showEditModal: false }))
    }
  }

  // Delete device in group (top table)
  const handleDeleteDeviceInGroup = (device) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: device }))
  }

  const confirmDeleteDeviceInGroup = async () => {
    const device = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      const response = await makeRequest({
        method: 'PATCH',
        url: `/management/devices/${device.deviceId}`,
        headers: getAuthHeaders(),
        data: {},
        alertResult: true
      })

      if (response.status === 200) {
        await onRefreshDevices()
        await onRefreshDevicesGroups()
        showAlert({ message: t('mgmt.devicesGroups.deletedOk'), icon: 'success' })
      }
    } catch (err) {
      console.error('Error deleting device:', err)
      showAlert({ message: t('mgmt.devicesGroups.errDelete'), icon: 'error' })
    }
  }

  const handleExportExcel = () => {
    try {
      const dataToExport = devices.map(device => ({
        'Device Identifier': device.identifier,
        'Device Type': device.type,
        'Group Name': device.groupName,
        'Manager Device': device.managerDevice,
        'App Version': device.appVersion,
        'Last sync': device.lastSync
      }))

      const worksheet = XLSXUtils.json_to_sheet(dataToExport)
      const workbook = XLSXUtils.book_new()
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Devices')

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `devices_${new Date().toISOString().split('T')[0]}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)

      toast.success(t('mgmt.common.excelDownloaded'))
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      toast.error(t('mgmt.common.excelFailed'))
    }
  }

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF()

      doc.setFontSize(16)
      doc.text(t('mgmt.devicesGroups.reportTitle'), 14, 15)
      doc.setFontSize(10)
      doc.text(t('mgmt.common.generatedOn', { date: new Date().toLocaleDateString() }), 14, 25)

      const tableColumn = [
        'Device Identifier',
        'Device Type',
        'Group Name',
        'Manager Device',
        'App Version',
        'Last sync'
      ]

      const tableRows = devices.map(device => [
        device.identifier,
        device.type,
        device.groupName,
        device.managerDevice,
        device.appVersion,
        device.lastSync
      ])

      // @ts-ignore
      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      })

      doc.save(`devices_${new Date().toISOString().split('T')[0]}.pdf`)

      toast.success(t('mgmt.common.pdfDownloaded'))
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      toast.error(t('mgmt.common.pdfFailed'))
    }
  }

  const handleChangeSelect = (e) => {
    setState(s => ({
      ...s,
      selectedDeviceGroup: e.target.value
    }))
  }

  const handleSearchChange = (e) => {
    setState(s => ({ ...s, searchTermDevicesGroups: e.target.value }))
  }

  const devicesGroupsTableData = {
    hasCheckbox: true,
    columns: [
      { key: 'type', labelKey: 'mgmt.devicesGroups.cols.type' },
      { key: 'identifier', labelKey: 'mgmt.devicesGroups.cols.identifier' },
      { key: 'groupName', labelKey: 'mgmt.devicesGroups.cols.groupName' },
      { key: 'managerDevice', labelKey: 'mgmt.devicesGroups.cols.managerDevice', cell: (value) => value ? t('mgmt.common.yes') : t('mgmt.common.no') },
      { key: 'assignedActivities', labelKey: 'mgmt.devicesGroups.cols.assignedActivities' },
      { key: 'appVersion', labelKey: 'mgmt.devicesGroups.cols.appVersion' },
      {
        key: 'lastSync',
        labelKey: 'mgmt.devicesGroups.cols.lastSync',
        cell: (value) => parseDate(value)
      },
      {
        key: 'actions',
        labelKey: 'mgmt.devicesGroups.cols.manage',
        cell: (value, row) => (
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setState(s => ({ ...s, showEditModal: true, selectedDevice: row }))}
              title={t('common.edit')} className='p-1 rounded transition'
            >
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDeleteDeviceInGroup(row)} title={t('common.delete')} className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
            <button onClick={() => openActivitiesModal(row)} title={t('mgmt.devicesGroups.deviceActivitiesTitle')} className='p-1 rounded transition'>
              <ListBulletIcon className='w-5 h-5 text-emerald-600' />
            </button>
          </div>
        )
      }
    ],
    rows: [...devicesInGroupsMapping]
  }

  const filteredDevicesGroups = {
    ...devicesGroupsTableData,
    rows: devicesGroupsTableData.rows.filter(row => {
      const searchTerm = state.searchTermDevicesGroups.toLowerCase()

      const isMatchingText =
        row.type.toLowerCase().includes(searchTerm) ||
        row.identifier.toLowerCase().includes(searchTerm) ||
        row.groupName.toLowerCase().includes(searchTerm) ||
        row.appVersion.toLowerCase().includes(searchTerm)

      const isMatchingGroup =
        state.selectedDeviceGroup === '' ||
        row.groupName === devicesGroupsSelectOptions.find(g => g.value === state.selectedDeviceGroup)?.label

      return isMatchingText && isMatchingGroup
    })
  }

  return (
    <>
      {/* Devices in Groups */}
      <ActionCardComponent>
        <input
          type='text'
          placeholder={t('mgmt.devicesGroups.searchGroup')}
          value={state.searchTermDevicesGroups}
          onChange={handleSearchChange}
          data-align='left'
          className='py-2 px-3 border rounded-md shadow-sm'
        />
        <SelectComponent
          id='selectedDeviceGroup'
          data-align='left'
          name='selectedDeviceGroup'
          value={state.selectedDeviceGroup || ''}
          onChange={handleChangeSelect}
        >
          <option key='' value=''>{t('mgmt.devicesGroups.allGroups')}</option>
          {devicesGroupsSelectOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </SelectComponent>

        <a
          href={LATEST_APK_URL}
          download
          className='py-2 rounded-lg shadow-md flex items-center gap-2 px-4 rounded-md min-w-max bg-greenPrimary text-white hover:bg-green-700 transition'
        >
          <ArrowDownTrayIcon className='w-5 h-5' />
          <span className='font-medium'>{t('mgmt.devicesGroups.downloadApk')}</span>
        </a>
        <ButtonComponent onClick={() => setState(s => ({ ...s, isQrModalOpen: true }))}>
          <DevicePhoneMobileIcon className='w-5 h-5' />
          <span className='font-medium'>{t('mgmt.devicesGroups.registerDevice')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportExcel}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span className='font-medium'>{t('common.exportExcel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span className='font-medium'>{t('mgmt.common.exportPdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={filteredDevicesGroups} defaultSortColumn={filteredDevicesGroups.columns[6]?.key} defaultSortOrder={filteredDevicesGroups.columns[6]?.orderBy || 'desc'} />

      {/* Modals */}
      <FarmQRModal
        isOpen={isQrModalOpen}
        onClose={() => setState(s => ({ ...s, isQrModalOpen: false }))}
        farmId={user.farmId}
        farmName={user.farmName}
      />

      {showEditModal && (
        <EditDeviceModal
          device={selectedDevice}
          groups={devicesGroupsSelectOptions}
          onClose={() => setState(s => ({ ...s, showEditModal: false, selectedDevice: {} }))}
          onSave={handleEditDevice}
        />
      )}

      <DeviceActivitiesModal
        isOpen={showActivitiesModal}
        device={state.selectedDeviceForActivities}
        activities={activities.filter(activity => activity?.isActive !== false)}
        selectedActivityIds={state.selectedActivityIds}
        onToggleActivity={handleToggleActivity}
        onClose={closeActivitiesModal}
        onSave={handleSaveDeviceActivities}
        isSaving={state.isSavingActivities}
      />

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.devicesGroups.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {state.pendingDelete?.groupName
              ? t('mgmt.devicesGroups.removeConfirmWithGroup', { name: state.pendingDelete?.identifier ?? '', group: state.pendingDelete.groupName })
              : t('mgmt.devicesGroups.removeConfirm', { name: state.pendingDelete?.identifier ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDeleteDeviceInGroup}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default DevicesGroupsManagement
